import SelectRedesign from 'components/Select/SelectRedesign';
import { FiltersTreeContext } from 'contexts/ExploreFiltersTreeContext';
import React, { FunctionComponent, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getFiltersCounts } from 'selectors/envelopes';
import { getNavParamsByResourceMemo } from 'selectors/nav';
import { useSelector } from 'store';

interface DaysOutOfRetentionFilterProps {
  className?: string;
  resource: string;
  treeContext: React.Context<FiltersTreeContext>;
}

enum DateOption {
  initial = '',
  day = '1',
  week = '7',
  month = '30',
}

export const datesOutOfRetention: Record<string, string> = {
  [DateOption.day.toString()]: 'Day',
  [DateOption.week.toString()]: 'Week',
  [DateOption.month.toString()]: 'Month',
};

const DaysOutOfRetentionFilter: FunctionComponent<DaysOutOfRetentionFilterProps> = ({
  resource,
  className,
  treeContext,
}) => {
  const dispatch = useDispatch();
  const { treeParams, handleAddValueToTree, handleRemoveFieldFromTree } = useContext(treeContext);

  const navParams = useSelector((state) => getNavParamsByResourceMemo(state, resource));

  const filterCounts = useSelector(getFiltersCounts).retention_windows;
  const { date_range: dateRange, days_from_oor: dOorParam } = treeParams;

  const [timeFromOorTree] = dOorParam || [];
  const timeFromOor = timeFromOorTree || (navParams.days_from_oor as string) || '';
  const [createdAfter, createdBefore] =
    dateRange?.[0].split('<>') || [
      navParams.created_after as string,
      navParams.created_before as string,
    ] ||
    [];

  useEffect(() => {
    if (!timeFromOor) return;
    if (createdAfter || createdBefore) {
      handleRemoveFieldFromTree('days_from_oor');
    }
  }, [createdAfter, createdBefore, dispatch, timeFromOor, handleRemoveFieldFromTree]);

  const handleDateOptionChanged = (dateOpt: string): void => {
    if (dateOpt === timeFromOor) {
      handleRemoveFieldFromTree('days_from_oor');
      return;
    }
    if (dateRange) {
      handleRemoveFieldFromTree('date_range');
    }

    handleAddValueToTree(dateOpt, 'days_from_oor', datesOutOfRetention[dateOpt], true);
  };
  return (
    <div className={`flex flex-col gap-2 mt-2 shadow-sm ${className || ''}`}>
      <div className="bg-litlingo-success-light bg-opacity-10 rounded ">
        <SelectRedesign
          className="bg-transparent border-none"
          valueClassName="text-white"
          optionClassname="text-litlingo-gray-6"
          onChange={(value): void => {
            handleDateOptionChanged(value);
          }}
          options={Object.entries(datesOutOfRetention).map(([value, label]) => ({ value, label }))}
          value={timeFromOor ?? DateOption.initial}
          placeholder="Select a date range"
          optionComponent={(
            value: string,
            label: string,
            onChange: (option: string) => void
          ): JSX.Element => (
            <button
              type="button"
              className={`w-full text-body text-left border-litlingo-gray-2 p-2 focus:outline-none hover:bg-litlingo-gray-1 ${
                timeFromOor === value ? 'font-bold cursor-default' : 'cursor-pointer'
              }`}
              onClick={(): void => onChange(value)}
            >
              <div className="flex flex-row justify-between">
                <span>{label}</span>
                <span className="text-litlingo-primary">
                  {/* @ts-ignore */}
                  {filterCounts.find((c) => c.value.toString() === value)?.count || 0}
                </span>
              </div>
            </button>
          )}
        />
      </div>
    </div>
  );
};

export default DaysOutOfRetentionFilter;
